import React, { useEffect, useRef, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import CytoscapeComponent from 'react-cytoscapejs';
import cytoscape from 'cytoscape';
import cytoscapeSvg from 'cytoscape-svg';
import fcose from 'cytoscape-fcose';
import { knownAddressService } from '../services/api';
import { formatDateGraph } from '../services/dateUtils';
import '../styles/Graph.css';

// Register cytoscape-svg plugin (safe to call multiple times — cytoscape guards against double-registration)
try { cytoscape.use(cytoscapeSvg); } catch (_) {}
// Register cytoscape-fcose — replaces the built-in 'cose' layout for 'auto' mode below.
// cose is a synchronous, ~O(n^2), single-threaded force simulation that could freeze the
// tab on larger graphs and wasn't deterministic between runs (visible "jumping" on every
// re-layout/tab-switch). fcose uses a spectral pre-layout instead of pure brute-force
// physics — much faster and far more stable between runs. See CLAUDE.md July 20 part 4.
try { cytoscape.use(fcose); } catch (_) {}

const ADDRESS_TYPES = ['contract', 'exchange', 'mixer', 'sanctioned', 'bridge', 'defi', 'other'];
const BLOCKCHAINS   = ['tron', 'ethereum', 'polygon'];

const KNOWN_COLORS = {
  contract:   '#a855f7',
  exchange:   '#f59e0b',
  mixer:      '#ef4444',
  sanctioned: '#dc2626',
  bridge:     '#06b6d4',
  defi:       '#10b981',
  other:      '#6b7280',
};

const KNOWN_ICONS = {
  contract:   '📜',
  exchange:   '🏦',
  mixer:      '🔀',
  sanctioned: '🚫',
  bridge:     '🌉',
  defi:       '⚡',
  other:      '🏷️',
};

// Layout modes
const LAYOUT_MODES = [
  { key: 'auto',       label: '⊙ Auto',       title: 'Force-directed layout (fcose)' },
  { key: 'tracer',     label: '→ Tracer',      title: 'Column layout by hop depth — follows the money left to right' },
  { key: 'visualizer', label: '◎ Visualizer',  title: 'Radial layout — start address at centre, inflows green, outflows red' },
];

// ── Colour helpers ────────────────────────────────────────────────────
function getNodeBgColor(node, knownMap, startAddress, monitoredAddresses) {
  const addr  = node.id || node.address || '';
  const known = knownMap[addr];
  if (startAddress && addr === startAddress) return '#00d9ff';
  if (monitoredAddresses?.has(addr)) return '#f97316'; // orange — watched address
  if (known) return known.color || KNOWN_COLORS[known.address_type] || '#6b7280';
  const score = node.risk_score || 0;
  if (score >= 7.5) return '#ff3b3b';
  if (score >= 5)   return '#ff8c00';
  if (score >= 2.5) return '#e8c200';
  return '#1e8c45';
}

function getNodeBorderColor(node, knownMap, startAddress, monitoredAddresses) {
  const addr  = node.id || node.address || '';
  if (startAddress && addr === startAddress) return '#ffffff';
  if (monitoredAddresses?.has(addr)) return '#fed7aa'; // light orange border
  if (knownMap[addr]) return '#ffffff';
  return 'rgba(255,255,255,0.4)';
}

function getNodeShape(node, knownMap, startAddress, layoutMode, monitoredAddresses) {
  const addr = node.id || node.address || '';
  if (startAddress && addr === startAddress) return layoutMode === 'tracer' ? 'round-rectangle' : 'star';
  if (monitoredAddresses?.has(addr)) return layoutMode === 'tracer' ? 'round-rectangle' : 'pentagon';
  if (knownMap[addr]) return layoutMode === 'tracer' ? 'round-rectangle' : 'diamond';
  return layoutMode === 'tracer' ? 'round-rectangle' : 'ellipse';
}

function getNodeSize(node, knownMap, startAddress, monitoredAddresses) {
  const addr    = node.id || node.address || '';
  const isStart = startAddress && addr === startAddress;
  if (isStart) return 36;
  if (monitoredAddresses?.has(addr)) return 30;
  if (knownMap[addr]) return 28;
  const txCount = node.tx_count || 1;
  return Math.max(18, Math.min(48, 14 + txCount * 1.8));
}

function getNodeLabel(node, knownMap, startAddress, monitoredAddresses) {
  const addr  = node.id || node.address || '';
  const known = knownMap[addr];
  if (startAddress && addr === startAddress) return `★ ${addr.substring(0, 8)}…`;
  if (monitoredAddresses?.has(addr)) return `📡 ${addr.substring(0, 8)}…`;
  if (known?.name) return `${known.name}${known.symbol ? ` (${known.symbol})` : ''}`;
  return `${addr.substring(0, 8)}…`;
}

function formatValue(val) {
  if (val >= 1000000) return `${(val / 1000000).toFixed(1)}M`;
  if (val >= 1000)    return `${(val / 1000).toFixed(1)}K`;
  if (val >= 1)       return val.toFixed(2);
  return val.toFixed(4);
}

// Edge colour relative to focal node (startAddress):
//   outflow from start → red (money leaving)
//   inflow  to   start → green (money arriving)
//   other             → dim blue-grey (auto mode default)
function getEdgeColor(source, target, startAddress, layoutMode) {
  if (layoutMode === 'auto') {
    const isStart = startAddress && (source === startAddress || target === startAddress);
    return isStart ? 'rgba(0,217,255,0.7)' : 'rgba(150,180,255,0.35)';
  }
  // tracer + visualizer: directional colour relative to startAddress
  if (startAddress) {
    if (source === startAddress) return 'rgba(239,68,68,0.75)';   // outflow — red
    if (target === startAddress) return 'rgba(74,222,128,0.75)';  // inflow  — green
    // multi-hop: keep outflow direction colour — edges flow away from start
    return 'rgba(239,68,68,0.45)';
  }
  return 'rgba(150,180,255,0.4)';
}

// ── Cytoscape stylesheet ──────────────────────────────────────────────
const CY_STYLESHEET = [
  {
    selector: 'node',
    style: {
      'background-color': 'data(bgColor)',
      'border-color':     'data(borderColor)',
      'border-width':     'data(borderWidth)',
      'width':            'data(size)',
      'height':           'data(size)',
      'shape':            'data(shape)',
      'label':            'data(label)',
      'color':            '#ffffff',
      'font-size':        'data(fontSize)',
      'font-family':      'data(fontFamily)',
      'font-weight':      'data(fontWeight)',
      'text-valign':      'bottom',
      'text-halign':      'center',
      'text-margin-y':    4,
      'text-outline-color': 'rgba(0,0,0,0.85)',
      'text-outline-width': 2,
    },
  },
  {
    selector: 'node:selected',
    style: {
      'border-width': 5,
      'border-color': '#00d9ff',
    },
  },
  {
    selector: 'edge',
    style: {
      'width':              'data(width)',
      'line-color':         'data(lineColor)',
      'target-arrow-color': 'data(lineColor)',
      'target-arrow-shape': 'triangle',
      'arrow-scale':        0.7,
      'curve-style':        'unbundled-bezier',
      'control-point-distances': 'data(cpDist)',
      'control-point-weights':   0.5,
      'label':              'data(label)',
      'font-size':          9,
      'color':              '#aac8ff',
      'text-outline-color': 'rgba(0,0,0,0.7)',
      'text-outline-width': 2,
      'text-rotation':      'autorotate',
      'text-opacity':       'data(labelOpacity)',
    },
  },
  {
    selector: 'edge:selected',
    style: {
      'width': 4,
      'line-color': '#00d9ff',
      'target-arrow-color': '#00d9ff',
    },
  },
  {
    selector: 'node.search-flash',
    style: {
      'border-width': 7,
      'border-color': '#fbbf24',
      'overlay-color': '#fbbf24',
      'overlay-opacity': 0.25,
      'overlay-padding': 8,
    },
  },
  {
    selector: 'edge.search-flash',
    style: {
      'width': 6,
      'line-color': '#fbbf24',
      'target-arrow-color': '#fbbf24',
      'overlay-color': '#fbbf24',
      'overlay-opacity': 0.2,
      'overlay-padding': 6,
    },
  },
  // TX direction filter — dim everything that doesn't match the active direction
  {
    selector: 'node.tx-dim',
    style: { 'opacity': 0.15 },
  },
  {
    selector: 'edge.tx-dim',
    style: { 'opacity': 0.08 },
  },
  {
    selector: 'node.tx-direction-match',
    style: { 'opacity': 1 },
  },
  {
    selector: 'edge.tx-direction-match',
    style: { 'opacity': 1 },
  },
  // Timeframe filter — dim elements outside the selected date range
  {
    selector: 'node.date-dim',
    style: { 'opacity': 0.12 },
  },
  {
    selector: 'edge.date-dim',
    style: { 'opacity': 0.06 },
  },
  {
    selector: 'node.date-match',
    style: { 'opacity': 1 },
  },
  {
    selector: 'edge.date-match',
    style: { 'opacity': 1 },
  },
  // Dust filter — dim low-value edges (and nodes whose every edge is dust) below
  // dustThreshold. Independent axis from tx-dim/date-dim, so it composes with them
  // rather than overriding — Cytoscape opacity from multiple classes just combines
  // via whichever rule matched most recently applied, same pattern as the other two
  // filters. See CLAUDE.md Aug 26 2026 graph noise-reduction entry.
  {
    selector: 'node.dust-dim',
    style: { 'opacity': 0.1 },
  },
  {
    selector: 'edge.dust-dim',
    style: { 'opacity': 0.05 },
  },
];

// Props:
//   data            - { nodes, edges }
//   startAddress    - scammer wallet address (protected from removal)
//   hiddenEdges     - Set of edge IDs to hide (controlled by parent, persisted)
//   hiddenNodes     - Set of node IDs to hide (controlled by parent, persisted)
//   onViewStateChange(newHiddenEdges, newHiddenNodes) - called whenever hidden sets change
//   onNodeSelect(selectedNode|null) - called whenever the selected node changes, so the parent
//                                      renders the node detail card in its sidebar (this is the
//                                      ONLY place node details are shown - there is intentionally
//                                      no in-canvas floating panel)
//   onEdgeSelect(selectedEdge|null) - called whenever the selected edge changes, so the parent
//                                      renders the transaction detail card in its sidebar (this is
//                                      the ONLY place edge details are shown - no in-canvas panel)
export default function Graph({ data, startAddress, hiddenEdges = new Set(), hiddenNodes = new Set(), onViewStateChange, onNodeSelect, onEdgeSelect, txFilter = 'all', onTxFilterChange, investigationId, isFocusedView = false, dateFrom = '', dateTo = '', onDateFromChange, onDateToChange, monitoredAddresses = new Set() }) {
  const cyRef          = useRef(null);
  const tooltipRef     = useRef(null);
  // Tracks the currently-running Cytoscape layout instance (if any). See runLayout below --
  // this guards against a second layout starting before the first has settled, which was
  // corrupting Cytoscape's internal state (repeating "Cannot read properties of null
  // (reading 'notify')" crash loop) and causing both jumping (two layouts fighting over
  // positions) and tab freezes (the same failing internal call hammered repeatedly).
  // See CLAUDE.md July 20 part 5.
  const layoutInstanceRef = useRef(null);

  // Tracks whether the *upcoming* layout run is a genuine structural/user-driven change
  // (first mount, layout-mode switch, or start-address change) vs. a routine background
  // data refresh (e.g. the wallet monitor's silent auto-refresh re-fetching this
  // investigation's graph on every SSE new_tx event, or a manual "Fetch Blockchain Data"
  // click). Previously EVERY data refresh ran a full fit:true/animate:true layout pass,
  // which reset the investigator's zoom/pan and visually looked identical to the
  // "erratic/jumping" graph bug even after the cose->fcose swap -- that swap only fixed
  // layout *determinism*, it never addressed *how often* a disruptive full relayout+refit
  // fires. See CLAUDE.md graph-instability entries (July 23 2026).
  const layoutTriggerKeyRef = useRef(null);
  // Tracks the node-id set from the previous elements build. layoutTriggerKeyRef (above) only
  // catches layoutMode/startAddress changes -- it does NOT catch the case where the *dataset
  // itself* has been swapped for an unrelated one while mode/startAddress stay the same (e.g.
  // HopTracerPanel/TracePage re-running a trace into the same mounted Graph instance with a
  // completely different node set). Without a fit in that case, the old pan/zoom stays put while
  // fresh nodes -- with no prior cached position and fcose's randomize:false -- land wherever
  // cytoscape's default (0,0)-ish placement puts them, which can render far outside the visible
  // viewport (reported: graph appears off-screen to the left after a hop trace). Fixed by also
  // treating a low node-id overlap between the previous and new element sets as structural.
  const prevNodeIdsRef = useRef(new Set());

  // Complements prevNodeIdsRef's dataset-swap detection above: that catches a largely
  // *different* node set (overlap ratio < 0.5) and forces a full fit. It does NOT catch the
  // more common case of a background refresh adding just a couple of brand-new nodes on top of
  // an otherwise-unchanged graph (overlap ratio stays high, so isStructuralTrigger correctly
  // stays false and fit/animate correctly stay off) -- but fcose still has to place those new
  // nodes somewhere, and with no fit call at all they can land outside the current visible
  // extent: present in the graph, invisible on screen. maybeFitNewNodes (below, defined next to
  // the layout effect) tracks exactly which node ids are new since the last build and does a
  // small, non-disruptive viewport correction only if one of them actually lands off-screen.
  const pendingNewNodeIdsRef = useRef(new Set());
  const seenNodeIdsRef = useRef(null); // last build's raw node-id set, used only to diff for pendingNewNodeIdsRef
  const [selectedNode, setSelectedNode] = useState(null);
  const [selectedEdge, setSelectedEdge] = useState(null);

  // Mirror the selected node up to the parent (sidebar display) whenever it changes -
  // covers every code path that calls setSelectedNode (tap, Focus Start, Remove Node,
  // context-menu hide, Hide Highlighted, panel close) without needing to touch each one.
  useEffect(() => {
    onNodeSelect?.(selectedNode);
  }, [selectedNode, onNodeSelect]);

  // Mirror the selected edge up to the parent (sidebar display) whenever it changes -
  // same rationale as the node mirror above. There is no in-canvas edge panel.
  useEffect(() => {
    onEdgeSelect?.(selectedEdge);
  }, [selectedEdge, onEdgeSelect]);

  const [selectedSummary, setSelectedSummary] = useState({ nodes: 0, edges: 0 });
  const selectedSummaryTimerRef = useRef(null);
  const [knownMap, setKnownMap]         = useState({});
  const [knownLoaded, setKnownLoaded]   = useState(false);
  // Set when the bulk-lookup call (below) fails or times out. The graph itself still renders
  // fine in this case -- knownLoaded still flips true via .finally() -- but known-address
  // colours/labels/icons are silently missing without this, which could read as "the registry
  // data vanished" rather than "a request failed." See CLAUDE.md blank-canvas investigation
  // and the api.js bulkLookup timeout comment.
  const [knownLookupFailed, setKnownLookupFailed] = useState(false);
  const [elements, setElements]         = useState([]);
  const [layoutRunning, setLayoutRunning] = useState(false);
  // Default changed from 'auto' to 'tracer' (Aug 26 2026, see CLAUDE.md graph
  // noise-reduction entry): 'auto'/fcose is a generic force-directed layout with no
  // investigative structure, while 'tracer' is already built around hop-depth from
  // startAddress — inherently more legible for tracing. 'auto' remains available as
  // a fallback via the layout switcher, just no longer the opener.
  const [layoutMode, setLayoutMode]     = useState('tracer'); // 'auto' | 'tracer' | 'visualizer'

  // ── Dust / low-value edge filter ──────────────────────────────────────
  // Scammers and mixers commonly spray low-value "dust" transactions across many
  // addresses specifically to obfuscate a trace — right now every one of those
  // rendered at full visual weight, same as a significant transfer. Default is 0
  // (off) rather than an auto-enabled value: transaction value scale varies a lot
  // across chains/tokens (TRX vs ETH vs an 18-decimal ERC-20), so a single hardcoded
  // threshold could hide real signal on some chains while doing nothing on others.
  // Investigator sets it per-investigation via the toolbar control below. See
  // CLAUDE.md Aug 26 2026 graph noise-reduction entry.
  const [dustThreshold, setDustThreshold] = useState(0);

  // Right-click context menu — can target node OR edge
  const [ctxMenu, setCtxMenu] = useState(null); // { x, y, nodeId? | edgeId?, type: 'node'|'edge' }
  const ctxMenuRef = useRef(null);

  // Clamp the context menu to stay fully inside the viewport. Its exact size isn't
  // known until it renders (item count varies between node/edge and registry state),
  // so measure it post-render and nudge it back on-screen if it would otherwise
  // overflow the right or bottom edge (fix: floating panels were getting cut off when
  // right-clicking near the screen edge).
  //
  // Also unconditionally hides the hover tooltip here (fix: right-clicking a node/edge
  // while the mouse was already sitting over it left the hover tooltip showing, since
  // cxttap doesn't fire mouseout. .cy-tooltip's z-index (9999) sits above
  // .graph-ctx-menu's (1000), so the leftover tooltip rendered on top of and partially
  // obscured the freshly-opened context menu. Centralising the hideTooltip() call here
  // — this effect already runs on every ctxMenu open AND close — covers it regardless
  // of which handler (node cxttap, edge cxttap) triggered the open, rather than having
  // to remember to call it in each one individually.
  useEffect(() => {
    hideTooltip();
    if (!ctxMenu) return;
    const el = ctxMenuRef.current;
    if (!el) return;
    const margin = 8;
    const rect = el.getBoundingClientRect();
    let { x, y } = ctxMenu;
    if (x + rect.width > window.innerWidth - margin)  x = Math.max(margin, window.innerWidth - rect.width - margin);
    if (y + rect.height > window.innerHeight - margin) y = Math.max(margin, window.innerHeight - rect.height - margin);
    if (x !== ctxMenu.x) el.style.left = `${x}px`;
    if (y !== ctxMenu.y) el.style.top  = `${y}px`;
  }, [ctxMenu]);

  // ── Search bar (address / TX hash) ────────────────────────────────
  const [searchQuery, setSearchQuery] = useState('');
  const [searchError, setSearchError] = useState(null);
  const searchFlashTimerRef = useRef(null);
  useEffect(() => () => { if (searchFlashTimerRef.current) clearTimeout(searchFlashTimerRef.current); }, []);

  // ── Refs to track latest hidden sets ────────────────────────────
  // Layout stop-callbacks are closures created at run time and cannot
  // safely read props or state directly.  Keep refs in sync so that
  // applyVisibility always uses current values whenever it is called.
  const hiddenEdgesRef = useRef(hiddenEdges);
  const hiddenNodesRef = useRef(hiddenNodes);
  useEffect(() => {
    hiddenEdgesRef.current = hiddenEdges;
    hiddenNodesRef.current = hiddenNodes;
  }, [hiddenEdges, hiddenNodes]);

  // Stamps the current hidden sets onto the live cy instance.
  // Reads refs rather than closing over props/state, so it is safe to
  // call from layout stop callbacks (which are closures created earlier).
  const applyVisibility = useCallback((cy) => {
    if (!cy) return;
    cy.nodes().forEach(n => { hiddenNodesRef.current.has(n.id()) ? n.hide() : n.show(); });
    cy.edges().forEach(e => { hiddenEdgesRef.current.has(e.id()) ? e.hide() : e.show(); });
  }, []);

  // Runs a Cytoscape layout, first stopping any layout still in-flight on this instance.
  // Starting a second layout before the first has settled was corrupting Cytoscape's
  // internal batching state -- see the layoutInstanceRef comment above. Every layout.run()
  // call in this component goes through here instead of calling cy.layout(cfg).run()
  // directly. cfg.stop (if provided) is wrapped so layoutInstanceRef is always cleared
  // once a layout genuinely finishes, whether it completed normally or was stopped early.
  const runLayout = useCallback((cy, cfg) => {
    if (!cy || cy.destroyed()) return;
    if (layoutInstanceRef.current) {
      try { layoutInstanceRef.current.stop(); } catch (_) { /* already stopped/destroyed */ }
      layoutInstanceRef.current = null;
    }
    const userStop = cfg.stop;
    const wrappedCfg = {
      ...cfg,
      stop: () => {
        layoutInstanceRef.current = null;
        userStop?.();
      },
    };
    setLayoutRunning(true);
    try {
      const layout = cy.layout(wrappedCfg);
      layoutInstanceRef.current = layout;
      layout.run();
    } catch (e) {
      console.error('Graph layout failed:', e);
      layoutInstanceRef.current = null;
      setLayoutRunning(false);
    }
  }, []);

  // Add-to-registry modal
  const [regModal, setRegModal] = useState(null);
  const [regForm, setRegForm]   = useState({ address_type: 'contract', name: '', symbol: '', notes: '' });
  const [regSaving, setRegSaving]   = useState(false);
  const [regError,  setRegError]    = useState(null);
  const [regSuccess, setRegSuccess] = useState(null);

  // ── Step 1: bulk-lookup ──────────────────────────────────────────
  useEffect(() => {
    setKnownLookupFailed(false);
    if (!data?.nodes?.length) { setKnownLoaded(true); return; }
    const addrs = data.nodes.map(n => n.id || n.address).filter(Boolean);
    if (!addrs.length) { setKnownLoaded(true); return; }
    knownAddressService.bulkLookup(addrs)
      .then(res => { setKnownMap(res.data || {}); })
      .catch(() => { setKnownMap({}); setKnownLookupFailed(true); })
      .finally(() => setKnownLoaded(true));
  }, [data]);

  // ── Step 2: build Cytoscape elements ────────────────────────────
  // hiddenEdges/hiddenNodes are NOT in this dep array — visibility is
  // handled separately below so the layout never re-runs on hide/show.
  useEffect(() => {
    if (!data || !knownLoaded) return;

    setSelectedNode(null);
    setSelectedEdge(null);

    // Deduplicate nodes
    const seenNodeIds = new Set();
    const uniqueNodes = (data.nodes || []).filter(node => {
      const id = node.id || node.address || '';
      if (!id || seenNodeIds.has(id)) return false;
      seenNodeIds.add(id);
      return true;
    });

    // Deduplicate edges
    const seenEdgeIds = new Set();
    const uniqueEdges = (data.edges || []).filter((edge, i) => {
      const id = edge.id || `e${i}`;
      if (seenEdgeIds.has(id)) return false;
      seenEdgeIds.add(id);
      return true;
    });

    // Build node elements
    const nodeEls = uniqueNodes.map(node => {
      const addr    = node.id || node.address || '';
      const isStart = startAddress && addr === startAddress;
      const isMonitored = monitoredAddresses.has(addr);
      const known   = knownMap[addr];
      const baseSize = getNodeSize(node, knownMap, startAddress, monitoredAddresses);
      const w = layoutMode === 'tracer' ? Math.max(baseSize, 80) : baseSize;
      const h = layoutMode === 'tracer' ? Math.max(baseSize * 0.55, 30) : baseSize;
      return {
        group: 'nodes',
        data: {
          id:          addr,
          label:       getNodeLabel(node, knownMap, startAddress, monitoredAddresses),
          bgColor:     getNodeBgColor(node, knownMap, startAddress, monitoredAddresses),
          borderColor: getNodeBorderColor(node, knownMap, startAddress, monitoredAddresses),
          borderWidth: isStart || isMonitored || known ? 3 : 2,
          size:        baseSize,
          width:       w,
          height:      h,
          shape:       getNodeShape(node, knownMap, startAddress, layoutMode, monitoredAddresses),
          fontSize:    isStart ? 14 : (isMonitored || known ? 13 : 11),
          fontFamily:  (known && !isMonitored) ? 'system-ui, sans-serif' : 'monospace',
          fontWeight:  (isStart || isMonitored || !!known) ? 'bold' : 'normal',
          _raw:        node,
          _known:      known || null,
        },
      };
    });

    // Build edge elements — fan out parallel edges with unbundled-bezier
    const nodeIdSet = new Set(uniqueNodes.map(n => n.id || n.address || ''));

    const pairCount = {};
    const pairIndex = {};
    uniqueEdges.forEach((edge) => {
      const src = edge.from || edge.source;
      const tgt = edge.to   || edge.target;
      if (!nodeIdSet.has(src) || !nodeIdSet.has(tgt)) return;
      const key = `${src}||${tgt}`;   // directed key — A→B and B→A fan out separately
      pairCount[key] = (pairCount[key] || 0) + 1;
    });

    const edgeEls = uniqueEdges
      .map((edge, i) => {
        const source = edge.from || edge.source;
        const target = edge.to   || edge.target;
        if (!nodeIdSet.has(source) || !nodeIdSet.has(target)) return null;
        const val   = edge.value || 0;
        // In visualizer mode, scale edge width more aggressively to show value flow
        const width = layoutMode === 'visualizer'
          ? Math.max(1, Math.min(8, 1 + Math.log1p(val) * 0.9))
          : Math.max(1, Math.min(6, 1 + Math.log1p(val) * 0.7));

        const key   = `${source}||${target}`;
        const total = pairCount[key] || 1;
        pairIndex[key] = (pairIndex[key] || 0);
        const idx   = pairIndex[key];
        pairIndex[key]++;
        const step   = 60;
        const offset = total > 1 ? (idx - (total - 1) / 2) * step : 0;

        return {
          group: 'edges',
          data: {
            id:           edge.id || `e${i}`,
            source,
            target,
            width,
            lineColor:    getEdgeColor(source, target, startAddress, layoutMode),
            label:        val > 0 ? formatValue(val) : '',
            labelOpacity: total > 3 ? 0 : 1,
            cpDist:       offset,
            _raw:         edge,
          },
        };
      })
      .filter(Boolean);

    // Diff against the ids seen in the previous build so the layout effect can nudge the
    // viewport if any brand-new node lands off-screen on a non-structural (background) refresh
    // -- see pendingNewNodeIdsRef comment above. Kept separate from prevNodeIdsRef (which drives
    // the coarser whole-dataset-swap check above) since this needs the raw incoming node set.
    const incomingNodeIds = new Set(uniqueNodes.map(n => n.id || n.address || ''));
    if (seenNodeIdsRef.current) {
      const added = new Set();
      incomingNodeIds.forEach(id => { if (!seenNodeIdsRef.current.has(id)) added.add(id); });
      pendingNewNodeIdsRef.current = added;
    }
    seenNodeIdsRef.current = incomingNodeIds;

    setElements([...nodeEls, ...edgeEls]);
  }, [data, startAddress, knownMap, knownLoaded, layoutMode, monitoredAddresses]); // eslint-disable-line react-hooks/exhaustive-deps

  // On a non-structural (background) layout run, do a small viewport correction ONLY if a
  // brand-new node from this refresh actually landed outside the currently-visible extent --
  // otherwise leaves the investigator's view completely undisturbed. Fits the whole graph (not
  // just the new node) so the correction reads as "the view eased out a little" rather than
  // jumping to focus on an arbitrary new node the investigator wasn't looking at.
  const maybeFitNewNodes = useCallback((cy, isStructural) => {
    if (!cy || cy.destroyed() || isStructural) return; // structural triggers already fit fully
    const ids = pendingNewNodeIdsRef.current;
    pendingNewNodeIdsRef.current = new Set(); // consume regardless of outcome
    if (!ids || ids.size === 0) return;
    const newColl = cy.nodes().filter(n => ids.has(n.id()));
    if (newColl.length === 0) return;
    const ext = cy.extent();
    const bb = newColl.boundingBox();
    const outside = bb.x1 < ext.x1 || bb.x2 > ext.x2 || bb.y1 < ext.y1 || bb.y2 > ext.y2;
    if (outside) {
      cy.animate({ fit: { eles: cy.elements(), padding: 60 } }, { duration: 400 });
    }
  }, []);

  // ── Run layout when elements change ─────────────────────────────
  useEffect(() => {
    const cy = cyRef.current;
    if (!cy || cy.destroyed() || elements.length === 0) return;

    // Only fit/animate on a genuine structural or user-driven trigger (first mount,
    // layout-mode switch, or start-address change). A routine data refresh -- silent
    // monitor auto-refresh, or a manual "Fetch Blockchain Data" re-pull -- still needs
    // to run the layout so any brand-new nodes get positioned, but must NOT reset the
    // investigator's zoom/pan or replay the settle animation, or it looks exactly like
    // the "erratic/jumping" bug even with a fully deterministic layout algorithm. See
    // the layoutTriggerKeyRef comment above and CLAUDE.md graph-instability entries.
    const triggerKey = `${layoutMode}::${startAddress || ''}`;
    const keyChanged = layoutTriggerKeyRef.current === null || layoutTriggerKeyRef.current !== triggerKey;
    layoutTriggerKeyRef.current = triggerKey;

    // Dataset-swap detection (see prevNodeIdsRef comment above). Compares the new node-id set
    // against the previous one by overlap ratio (intersection / union) rather than exact equality,
    // since incremental refreshes (a few new TXs added) SHOULD stay non-structural -- only a
    // largely-unrelated node set (a fresh/different trace result) should force a fit.
    const currentNodeIds = new Set(elements.filter(el => el.group === 'nodes').map(el => el.data.id));
    const prevNodeIds = prevNodeIdsRef.current;
    let overlapRatio = 1;
    if (prevNodeIds.size > 0 || currentNodeIds.size > 0) {
      const union = new Set([...prevNodeIds, ...currentNodeIds]);
      let intersectionCount = 0;
      currentNodeIds.forEach(id => { if (prevNodeIds.has(id)) intersectionCount++; });
      overlapRatio = union.size ? intersectionCount / union.size : 1;
    }
    const datasetSwapped = overlapRatio < 0.5;
    prevNodeIdsRef.current = currentNodeIds;

    const isStructuralTrigger = keyChanged || datasetSwapped;

    let layoutCfg;

    if (layoutMode === 'tracer') {
      // Breadthfirst: columns left→right rooted at startAddress.
      // directed:false so BFS depth is measured by graph distance via ANY edge
      // direction (not just forward/outflow) - graphs with inflow edges into the
      // root would otherwise leave those nodes "unreachable" in a forward-only walk
      // and collapse them into degenerate positions (fixed June 17 2026, see CLAUDE.md).
      layoutCfg = {
        name:           'breadthfirst',
        directed:       false,
        roots:          startAddress ? [`#${startAddress}`] : undefined,
        spacingFactor:  1.4,
        avoidOverlap:   true,
        animate:        isStructuralTrigger,
        animationDuration: 500,
        fit:            isStructuralTrigger,
        padding:        50,
        // Rotate 90° so hops go left→right instead of top→bottom
        // breadthfirst doesn't support horizontal natively — we rotate post-layout
        stop: () => {
          // Rotate the graph: swap x↔y, mirror y
          const cyi = cyRef.current;
          if (!cyi) return;
          cyi.nodes().forEach(n => {
            const p = n.position();
            n.position({ x: p.y * 1.6, y: p.x * 0.9 });
          });
          // Only re-fit the viewport on a structural trigger -- see comment above. This
          // manual cyi.fit() call is separate from layoutCfg.fit (breadthfirst's own
          // internal fit only applies pre-rotation) and was previously unconditional,
          // which reset the camera on every background refresh regardless of the flag.
          if (isStructuralTrigger) cyi.fit(50);
          applyVisibility(cyi); // re-stamp hidden state after layout rebuilds positions
          bindNodeEdgeHandlers(cyi); // re-bind direct tap/cxttap/dblclick handlers (workaround)
          maybeFitNewNodes(cyi, isStructuralTrigger); // correct only if a new node landed off-screen
          setLayoutRunning(false);
        },
      };
    } else if (layoutMode === 'visualizer') {
      // Concentric: start address in centre, rings by degree.
      // Start gets a value far above any other node's possible value so it always
      // lands in its own innermost ring regardless of levelWidth bucketing; other
      // nodes are ranked by total degree (varies more across nodes than tx_count
      // alone) with a small levelWidth so distinct degree values actually separate
      // into distinct rings (fixed June 17 2026, see CLAUDE.md - previously start's
      // value of 10 vs a typical leaf's value of 9 fell inside one levelWidth=2 band
      // and got merged into the same ring as everything else).
      layoutCfg = {
        name:          'concentric',
        concentric:    (node) => {
          if (startAddress && node.id() === startAddress) return 100000;
          const raw = node.data('_raw') || {};
          const degree = (raw.in_degree || 0) + (raw.out_degree || 0);
          return Math.max(1, degree || raw.tx_count || 1);
        },
        levelWidth:    () => 1,
        minNodeSpacing: 30,
        spacingFactor:  1.6,
        animate:        isStructuralTrigger,
        animationDuration: 500,
        fit:            isStructuralTrigger,
        padding:        50,
        stop: () => {
          applyVisibility(cyRef.current);
          bindNodeEdgeHandlers(cyRef.current);
          maybeFitNewNodes(cyRef.current, isStructuralTrigger);
          setLayoutRunning(false);
        },
      };
    } else {
      // Auto: fcose force-directed (swapped from 'cose' -- see CLAUDE.md July 20 part 4).
      // randomize:false lets fcose reuse each node's existing position as a starting point
      // when one exists, instead of always scattering and re-settling from scratch -- this
      // is the main lever against the "screen jumping" symptom. quality:'default' balances
      // layout quality against speed; use 'draft' instead if very large graphs still feel slow.
      layoutCfg = {
        name:          'fcose',
        quality:       'default',
        animate:       isStructuralTrigger,
        animationDuration: 600,
        randomize:     false,
        nodeRepulsion: () => 8000,
        idealEdgeLength: () => 160,
        edgeElasticity: () => 0.45,
        gravity:       0.25,
        fit:           isStructuralTrigger,
        padding:       40,
        stop: () => {
          applyVisibility(cyRef.current);
          bindNodeEdgeHandlers(cyRef.current);
          maybeFitNewNodes(cyRef.current, isStructuralTrigger);
          setLayoutRunning(false);
        },
      };
    }

    runLayout(cy, layoutCfg);
  }, [elements]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Apply visibility without re-layout (fix #1) ──────────────────
  // applyVisibility is also called inside every layout stop callback so
  // that scenarios where this effect already fired before cy had any nodes
  // (initial load, remount, tab-switch back) are corrected the moment the
  // layout finishes and cy is fully populated.
  useEffect(() => {
    applyVisibility(cyRef.current);
  }, [hiddenEdges, hiddenNodes, applyVisibility]);

  // ── TX direction filter — apply/clear dim classes ────────────────
  // Keyed on (selectedNode, txFilter). When a node is selected and filter
  // is 'incoming' or 'outgoing', dims every edge that isn't that node's
  // in/out-edge and dims nodes not connected by those matching edges.
  // Resets to 'all' automatically when selectedNode clears (handled by
  // the parent resetting txFilter via onTxFilterChange).
  useEffect(() => {
    const cy = cyRef.current;
    if (!cy) return;

    // Remove all direction-filter classes first
    cy.elements().removeClass('tx-dim tx-direction-match');

    if (!selectedNode || txFilter === 'all') return;

    const nodeId = selectedNode.id;
    const matchingEdges = cy.edges().filter(e => {
      if (txFilter === 'incoming') return e.data('target') === nodeId;
      if (txFilter === 'outgoing') return e.data('source') === nodeId;
      return false;
    });

    if (matchingEdges.length === 0) return;

    // Collect node ids touched by matching edges (the selected node + its neighbours in that direction)
    const matchingNodeIds = new Set([nodeId]);
    matchingEdges.forEach(e => {
      matchingNodeIds.add(e.data('source'));
      matchingNodeIds.add(e.data('target'));
    });

    // Dim everything, then un-dim matching edges and their endpoint nodes
    cy.edges().addClass('tx-dim');
    cy.nodes().addClass('tx-dim');
    matchingEdges.addClass('tx-direction-match').removeClass('tx-dim');
    cy.nodes().filter(n => matchingNodeIds.has(n.id())).addClass('tx-direction-match').removeClass('tx-dim');
  }, [selectedNode, txFilter]);

  // ── Timeframe filter — show only edges within date range ─────────
  // Keyed on (dateFrom, dateTo). Dims edges outside the range and dims
  // nodes that have no visible (in-range) edges, so the investigator
  // can isolate a specific time window of activity on the graph.
  useEffect(() => {
    const cy = cyRef.current;
    if (!cy) return;

    cy.elements().removeClass('date-dim date-match');

    if (!dateFrom && !dateTo) return;

    const from = dateFrom ? new Date(dateFrom).getTime() : null;
    // dateTo is a date-only string — extend to end of that day
    const to   = dateTo   ? new Date(dateTo + 'T23:59:59Z').getTime() : null;

    const matchingEdges = cy.edges().filter(e => {
      const raw = e.data('_raw') || {};
      if (!raw.timestamp) return false;
      const ts = new Date(raw.timestamp).getTime();
      if (isNaN(ts)) return false;
      if (from && ts < from) return false;
      if (to   && ts > to)   return false;
      return true;
    });

    // Collect node ids that have at least one in-range edge
    const matchingNodeIds = new Set();
    matchingEdges.forEach(e => {
      matchingNodeIds.add(e.data('source'));
      matchingNodeIds.add(e.data('target'));
    });

    cy.edges().addClass('date-dim');
    cy.nodes().addClass('date-dim');
    matchingEdges.addClass('date-match').removeClass('date-dim');
    cy.nodes().filter(n => matchingNodeIds.has(n.id())).addClass('date-match').removeClass('date-dim');
  }, [dateFrom, dateTo]);

  // ── Dust filter — dim edges below dustThreshold, and any node left with ────
  //    zero non-dust edges. Kept as a separate effect/axis from tx-dim/date-dim
  //    (a transaction can be simultaneously dust-dimmed and date-matched, etc).
  //    Depends on `elements` (not just dustThreshold) so it re-evaluates whenever
  //    the underlying dataset changes (new data fetched, hop trace re-run) —
  //    without that, a background refresh could leave stale dust-dim classes on
  //    elements that no longer exist post-rebuild, or fail to dim brand-new dust.
  useEffect(() => {
    const cy = cyRef.current;
    if (!cy) return;

    cy.elements().removeClass('dust-dim');

    if (!dustThreshold || dustThreshold <= 0) return;

    const dustEdges = cy.edges().filter(e => {
      const raw = e.data('_raw') || {};
      const val = raw.value || 0;
      return val > 0 && val < dustThreshold;
    });

    if (dustEdges.length === 0) return;

    dustEdges.addClass('dust-dim');

    // Only dim a node if ALL of its edges are dust — a node with at least one
    // significant edge stays fully visible even if some of its other edges are dust.
    cy.nodes().forEach(n => {
      if (startAddress && n.id() === startAddress) return; // never dim the start address
      const edges = n.connectedEdges();
      if (edges.length === 0) return;
      const allDust = edges.every(e => e.hasClass('dust-dim'));
      if (allDust) n.addClass('dust-dim');
    });
  }, [dustThreshold, elements, startAddress]);


  // Switching mode re-triggers element build (layoutMode in dep array above),
  // which then re-triggers the layout effect via the elements state change.
  const handleLayoutMode = useCallback((mode) => {
    setLayoutMode(mode);
  }, []);

  const refreshSelectedSummary = useCallback(() => {
    // IMPORTANT: react-cytoscapejs may invoke the `cy={...}` callback during its own
    // componentDidMount/componentDidUpdate lifecycle. Triggering React state updates
    // synchronously from that call stack can crash the app (minified React error #185).
    // Defer selection-summary updates to the next tick.
    if (selectedSummaryTimerRef.current) return;
    selectedSummaryTimerRef.current = setTimeout(() => {
      selectedSummaryTimerRef.current = null;
      const cy = cyRef.current;
      if (!cy) return;
      const nodeCount = cy.$('node:selected').length;
      const edgeCount = cy.$('edge:selected').length;
      setSelectedSummary({ nodes: nodeCount, edges: edgeCount });
    }, 0);
  }, []);

  const handleHideSelected = useCallback(() => {
    const cy = cyRef.current;
    if (!cy || !onViewStateChange) return;

    const selNodes = cy.$('node:selected');
    const selEdges = cy.$('edge:selected');

    const nodesToHide = selNodes
      .map(n => n.id())
      .filter(id => id && id !== startAddress);

    const edgesToHide = selEdges
      .map(e => e.id())
      .filter(Boolean);

    if (nodesToHide.length === 0 && edgesToHide.length === 0) return;

    const newHiddenNodes = new Set(hiddenNodes);
    nodesToHide.forEach(id => newHiddenNodes.add(id));

    const newHiddenEdges = new Set(hiddenEdges);
    edgesToHide.forEach(id => newHiddenEdges.add(id));

    // Clear selection and panels so you don't end up with a "selected but hidden" UI state.
    cy.elements().unselect();
    setSelectedNode(null);
    setSelectedEdge(null);
    setCtxMenu(null);
    setSelectedSummary({ nodes: 0, edges: 0 });

    onViewStateChange(newHiddenEdges, newHiddenNodes);
  }, [hiddenEdges, hiddenNodes, onViewStateChange, startAddress]);

  // ── Direct node/edge tap binding (workaround for delegated-dispatch bug) ──
  // CONFIRMED: cy's own delegated, selector-scoped listeners (cy.on('tap.cc', 'node', ...))
  // never get invoked by real pointer dispatch OR by cy.emit('tap') on the node - even though
  // the callback itself works perfectly when called directly (.callback(...)). See CLAUDE.md
  // for the full investigation. Workaround: bind tap / dblclick / cxttap directly on the
  // node/edge collections instead of delegating from the core. Tradeoff: a captured
  // cy.nodes()/cy.edges() collection does NOT pick up elements added after the fact, so this
  // must be re-applied every time the element set is rebuilt - called from every layout `stop`
  // callback below, mirroring the existing applyVisibility re-stamp pattern.
  const bindNodeEdgeHandlers = useCallback((cy) => {
    if (!cy) return;

    // NOTE (June 22 2026): switched from namespaced ('tap.cc' etc) to plain event names - see
    // CLAUDE.md, namespaced .on() registration is confirmed broken outright on Cytoscape 3.34.0,
    // independent of any preceding .off(). Safe to drop the namespace here since these are bound
    // on a fresh cy.nodes()/cy.edges() collection each call and .off('tap') only clears tap
    // listeners we ourselves registered (nothing else binds tap/dblclick/cxttap on elements).
    cy.nodes().off('tap').on('tap', (evt) => {
      const node = evt.target;
      setSelectedNode({ id: node.id(), _raw: node.data('_raw'), _known: node.data('_known') });
      setSelectedEdge(null);
      setCtxMenu(null);
      refreshSelectedSummary();
    });

    cy.nodes().off('dblclick').on('dblclick', (evt) => {
      cy.animate({ zoom: { level: 2.0, position: evt.target.position() }, duration: 400 });
    });

    cy.nodes().off('cxttap').on('cxttap', (evt) => {
      evt.originalEvent.preventDefault();
      const pos = evt.originalEvent;
      setCtxMenu({ x: pos.clientX, y: pos.clientY, nodeId: evt.target.id(), type: 'node' });
    });

    cy.edges().off('tap').on('tap', (evt) => {
      const edge = evt.target;
      const raw  = edge.data('_raw') || {};
      setSelectedEdge({
        id:           edge.id(),
        source:       edge.data('source'),
        target:       edge.data('target'),
        tx_hash:      raw.tx_hash      || edge.id(),
        value:        raw.value        || 0,
        token_symbol: raw.token_symbol || '',
        timestamp:    raw.timestamp    || '',
        status:       raw.status       || '',
      });
      setSelectedNode(null);
      setCtxMenu(null);
      refreshSelectedSummary();
    });

    cy.edges().off('cxttap').on('cxttap', (evt) => {
      evt.originalEvent.preventDefault();
      const pos = evt.originalEvent;
      const raw = evt.target.data('_raw') || {};
      setCtxMenu({
        x: pos.clientX,
        y: pos.clientY,
        edgeId: evt.target.id(),
        edgeTxHash: raw.tx_hash || evt.target.id(),
        edgeSource: evt.target.data('source'),
        edgeTarget: evt.target.data('target'),
        edgeValue:  raw.value || 0,
        edgeToken:  raw.token_symbol || '',
        type: 'edge',
      });
    });
  }, [refreshSelectedSummary]);

  // ── Wire up Cytoscape events ─────────────────────────────────────
  const handleCyRef = useCallback((cy) => {
    if (!cy) return;

    // react-cytoscapejs calls this `cy` prop callback on EVERY componentDidUpdate, not just on
    // mount (confirmed in its source: updateCytoscape() unconditionally calls props.cy(instance)
    // at the very end of every update). Graph re-renders on every selectedNode/selectedEdge/
    // ctxMenu change - i.e. on every single click - so without this guard, handleCyRef was
    // calling cy.off('.cc') and re-registering every handler from scratch on every click, often
    // while Cytoscape was still mid-dispatch of that same click's own event sequence. That churn
    // is what broke the node/edge info panels (selection highlight still happened natively, but
    // our state-setting handlers got unbound/rebound out from under the gesture) and is also the
    // likely cause of the previously-seen runaway "hidden" counts (corrupted multi-selection
    // tracking feeding into Hide Highlighted). Fix: only bind once per cy instance. The instance
    // itself only changes on a genuine remount (new investigation page), at which point these
    // closures naturally pick up the then-current startAddress etc.
    if (cyRef.current === cy) return;
    cyRef.current = cy;

    // NOTE (June 22 2026): namespaced event registration (the .cc suffix) was confirmed broken
    // outright on the installed Cytoscape 3.34.0 build - both .off(ns).on(ns, handler) and a bare
    // .on(ns, handler) with no preceding .off() silently fail to produce a callable listener (see
    // CLAUDE.md). Every binding below now uses plain, non-namespaced event names instead. The old
    // cy.off('.cc') call that used to live here is no longer needed: this function is guarded to
    // run only once per cy instance (see the cyRef.current === cy check above), so there is never
    // anything previously-bound on this instance to clear.

    // Deselect — core-level listener, no selector. NOT part of the delegated-dispatch bug
    // (confirmed fine on its own), left wired here.
    cy.on('tap', (evt) => {
      if (evt.target === cy) {
        setSelectedNode(null);
        setSelectedEdge(null);
        setCtxMenu(null);
        refreshSelectedSummary();
      }
    });

    // Node tap, edge tap, node dblclick-zoom, and node/edge right-click context menus are now
    // bound DIRECTLY on the node/edge collections via bindNodeEdgeHandlers (called from every
    // layout `stop` callback below) instead of being delegated from the core here — see the
    // comment above bindNodeEdgeHandlers for why.

    // Tooltip on hover
    cy.on('mouseover', 'node', (evt) => {
      const node = evt.target;
      const raw  = node.data('_raw') || {};
      const known = node.data('_known');
      const addr  = node.id();
      const isStart = startAddress && addr === startAddress;

      const lines = [
        isStart ? '★ START ADDRESS' : null,
        known   ? `${KNOWN_ICONS[known.address_type] || ''} ${known.name}${known.symbol ? ` (${known.symbol})` : ''}` : null,
        known   ? `Type: ${known.address_type}` : null,
        `Address: ${addr}`,
        `Risk Score: ${(raw.risk_score || 0).toFixed(2)} / 10`,
        `Transactions: ${raw.tx_count || 0}`,
        `In / Out: ${raw.in_degree || 0} / ${raw.out_degree || 0}`,
        'Right-click for options',
      ].filter(Boolean);

      showTooltip(evt.originalEvent, lines.join('\n'));
    });

    cy.on('mouseout', 'node', () => hideTooltip());

    cy.on('mouseover', 'edge', (evt) => {
      const raw = evt.target.data('_raw') || {};
      const val = raw.value || 0;
      const lines = [
        `Value: ${formatValue(val)}`,
        raw.token_symbol ? `Token: ${raw.token_symbol}` : (raw.title ? `Token: ${raw.title}` : null),
        raw.timestamp    ? `Date: ${formatDateGraph(raw.timestamp)}` : null,
        raw.tx_hash      ? `TX: ${raw.tx_hash.substring(0, 20)}…` : null,
        'Right-click to remove',
      ].filter(Boolean);
      showTooltip(evt.originalEvent, lines.join('\n'));
    });

    cy.on('mouseout', 'edge', () => hideTooltip());

    cy.on('pan zoom', () => hideTooltip());

    // Track multi-selection (highlighted nodes/edges)
    const onSelChange = () => refreshSelectedSummary();
    cy.on('select unselect', 'node, edge', onSelChange);
    refreshSelectedSummary();
  }, [startAddress, refreshSelectedSummary]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Tooltip helpers ──────────────────────────────────────────────
  // Same containing-block class of bug as the context menu (see CLAUDE.md): .cy-tooltip is
  // position:fixed, but was rendered inline under .tab-content/.graph-container (both
  // backdrop-filter ancestors), so its "fixed" coordinates were relative to that blurred
  // ancestor's box, not the real viewport. Now portaled to document.body below. Unlike the
  // context menu, this tooltip never had any edge-clamping at all (clientX/clientY + a fixed
  // offset, nothing else) - added the same getBoundingClientRect()-based clamp pattern used
  // for graph-ctx-menu so it can't overflow the right/bottom edge either.
  function showTooltip(event, text) {
    const tip = tooltipRef.current;
    if (!tip) return;
    tip.innerText = text;
    tip.style.display = 'block';
    const margin = 10;
    let x = event.clientX + 14;
    let y = event.clientY + 10;
    const rect = tip.getBoundingClientRect();
    if (x + rect.width > window.innerWidth - margin)   x = Math.max(margin, window.innerWidth - rect.width - margin);
    if (y + rect.height > window.innerHeight - margin) y = Math.max(margin, window.innerHeight - rect.height - margin);
    tip.style.left = `${x}px`;
    tip.style.top  = `${y}px`;
  }

  function hideTooltip() {
    const tip = tooltipRef.current;
    if (tip) tip.style.display = 'none';
  }

  // ── Toolbar handlers ─────────────────────────────────────────────
  const handleFit = () => cyRef.current?.fit(40);

  // Flash an element amber for a couple seconds so it's easy to spot after a search jump.
  const flashElement = useCallback((el) => {
    if (searchFlashTimerRef.current) clearTimeout(searchFlashTimerRef.current);
    el.addClass('search-flash');
    searchFlashTimerRef.current = setTimeout(() => {
      el.removeClass('search-flash');
      searchFlashTimerRef.current = null;
    }, 2200);
  }, []);

  // Search the current graph for a node address or TX hash, select + centre + flash it.
  // Matches nodes by id (address) and edges by tx_hash (falling back to edge id) - exact
  // match first, then case-insensitive substring. If the match is currently hidden via the
  // hide/restore system, it is automatically restored so the investigator can actually see it.
  const handleSearch = useCallback(() => {
    const cy = cyRef.current;
    const q  = searchQuery.trim();
    if (!cy || !q) return;
    setSearchError(null);
    const qLower = q.toLowerCase();

    // 1) Try node match — exact id first, then substring
    let nodeMatch = cy.nodes().filter(n => n.id().toLowerCase() === qLower);
    if (!nodeMatch.length) nodeMatch = cy.nodes().filter(n => n.id().toLowerCase().includes(qLower));

    if (nodeMatch.length) {
      const node = nodeMatch[0];
      if (hiddenNodes.has(node.id()) && onViewStateChange) {
        const newHiddenNodes = new Set(hiddenNodes);
        newHiddenNodes.delete(node.id());
        onViewStateChange(hiddenEdges, newHiddenNodes);
      }
      cy.elements().unselect();
      node.select();
      cy.animate({ zoom: { level: 2.0, position: node.position() }, duration: 500 });
      setSelectedNode({ id: node.id(), _raw: node.data('_raw'), _known: node.data('_known') });
      setSelectedEdge(null);
      setCtxMenu(null);
      flashElement(node);
      refreshSelectedSummary();
      return;
    }

    // 2) Try edge match — exact tx_hash/id first, then substring on tx_hash
    let edgeMatch = cy.edges().filter(e => {
      const raw = e.data('_raw') || {};
      return (raw.tx_hash || '').toLowerCase() === qLower || e.id().toLowerCase() === qLower;
    });
    if (!edgeMatch.length) {
      edgeMatch = cy.edges().filter(e => {
        const raw = e.data('_raw') || {};
        return (raw.tx_hash || '').toLowerCase().includes(qLower);
      });
    }

    if (edgeMatch.length) {
      const edge = edgeMatch[0];
      if (hiddenEdges.has(edge.id()) && onViewStateChange) {
        const newHiddenEdges = new Set(hiddenEdges);
        newHiddenEdges.delete(edge.id());
        onViewStateChange(newHiddenEdges, hiddenNodes);
      }
      const raw = edge.data('_raw') || {};
      cy.elements().unselect();
      edge.select();
      const mid = edge.midpoint ? edge.midpoint() : edge.source().position();
      cy.animate({ zoom: { level: 2.0, position: mid }, duration: 500 });
      setSelectedEdge({
        id:           edge.id(),
        source:       edge.data('source'),
        target:       edge.data('target'),
        tx_hash:      raw.tx_hash      || edge.id(),
        value:        raw.value        || 0,
        token_symbol: raw.token_symbol || '',
        timestamp:    raw.timestamp    || '',
        status:       raw.status       || '',
      });
      setSelectedNode(null);
      setCtxMenu(null);
      flashElement(edge);
      refreshSelectedSummary();
      return;
    }

    setSearchError('No matching address or transaction found in this graph.');
  }, [searchQuery, hiddenNodes, hiddenEdges, onViewStateChange, flashElement, refreshSelectedSummary]);

  const handleSearchClear = useCallback(() => {
    setSearchQuery('');
    setSearchError(null);
  }, []);

  const handleFocusStart = () => {
    const cy = cyRef.current;
    if (!cy || !startAddress) return;
    const node = cy.getElementById(startAddress);
    if (node.length) {
      cy.animate({ zoom: { level: 1.8, position: node.position() }, duration: 500 });
      cy.elements().unselect();
      node.select();
      setSelectedNode({ id: node.id(), _raw: node.data('_raw'), _known: node.data('_known') });
    }
  };

  const handleReLayout = () => {
    const cy = cyRef.current;
    if (!cy) return;
    // Re-trigger by forcing element rebuild with a dummy data touch isn't reliable,
    // so we re-run the layout directly with current mode settings.
    if (layoutMode === 'tracer') {
      runLayout(cy, {
        name:           'breadthfirst',
        directed:       false,
        roots:          startAddress ? [`#${startAddress}`] : undefined,
        spacingFactor:  1.4,
        avoidOverlap:   true,
        animate:        true,
        animationDuration: 500,
        fit:            true,
        padding:        50,
        stop: () => {
          const cyi = cyRef.current;
          if (!cyi) return;
          cyi.nodes().forEach(n => {
            const p = n.position();
            n.position({ x: p.y * 1.6, y: p.x * 0.9 });
          });
          cyi.fit(50);
          applyVisibility(cyi); // re-stamp hidden state after layout rebuilds positions
          bindNodeEdgeHandlers(cyi); // re-bind direct tap/cxttap/dblclick handlers (workaround)
          setLayoutRunning(false);
        },
      });
    } else if (layoutMode === 'visualizer') {
      runLayout(cy, {
        name:          'concentric',
        concentric:    (node) => {
          if (startAddress && node.id() === startAddress) return 100000;
          const raw = node.data('_raw') || {};
          const degree = (raw.in_degree || 0) + (raw.out_degree || 0);
          return Math.max(1, degree || raw.tx_count || 1);
        },
        levelWidth:    () => 1,
        minNodeSpacing: 30,
        spacingFactor:  1.6,
        animate:        true,
        animationDuration: 500,
        fit:            true,
        padding:        50,
        stop: () => { applyVisibility(cyRef.current); bindNodeEdgeHandlers(cyRef.current); setLayoutRunning(false); },
      });
    } else {
      runLayout(cy, {
        name: 'fcose',
        quality: 'default',
        animate: true,
        animationDuration: 600,
        randomize: true, // manual re-layout is an explicit "reshuffle" request, unlike the automatic layout effect above
        nodeRepulsion: () => 8000,
        idealEdgeLength: () => 160,
        edgeElasticity: () => 0.45,
        gravity: 0.25,
        fit: true,
        padding: 40,
        stop: () => { applyVisibility(cyRef.current); bindNodeEdgeHandlers(cyRef.current); setLayoutRunning(false); },
      });
    }
  };

  const handleExport = () => {
    const cy = cyRef.current;
    if (!cy) return;
    const png  = cy.png({ scale: 2, full: true, bg: '#060a14' });
    const link = document.createElement('a');
    link.href     = png;
    link.download = 'crypcrawler-graph.png';
    link.click();
  };

  const handleExportSvg = useCallback(() => {
    const cy = cyRef.current;
    if (!cy) return;
    try {
      const svgContent = cy.svg({ full: true, bg: '#060a14' });
      const blob = new Blob([svgContent], { type: 'image/svg+xml' });
      const url  = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href     = url;
      link.download = 'noctis-graph.svg';
      link.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) {
      console.error('SVG export failed:', e);
      alert('SVG export failed. Check that cytoscape-svg is installed (npm install cytoscape-svg).');
    }
  }, []);

  // Expose export functions on window so FocusedGraphView topbar buttons can trigger them
  useEffect(() => {
    if (!isFocusedView) return;
    window.__focusedGraphExportPng = handleExport;
    window.__focusedGraphExportSvg = handleExportSvg;
    return () => {
      delete window.__focusedGraphExportPng;
      delete window.__focusedGraphExportSvg;
    };
  }, [isFocusedView, handleExportSvg]); // eslint-disable-line react-hooks/exhaustive-deps

  // Expose a PNG data-URL getter (no download) for the MAIN graph view, so the
  // ExportPanel's "PDF Report" button can grab the current canvas snapshot and
  // ship it to the backend to embed in the report - without duplicating any of
  // Cytoscape's render/export logic outside this component.
  useEffect(() => {
    if (isFocusedView) return;
    window.__mainGraphExportPng = () => {
      const cy = cyRef.current;
      if (!cy) return null;
      try {
        return cy.png({ scale: 2, full: true, bg: '#060a14' });
      } catch (e) {
        console.error('Graph PNG snapshot for report failed:', e);
        return null;
      }
    };
    return () => { delete window.__mainGraphExportPng; };
  }, [isFocusedView]);

  // ── Node context menu handlers ────────────────────────────────────
  const handleCtxFocus = () => {
    const cy = cyRef.current;
    if (!cy || !ctxMenu) return;
    const node = cy.getElementById(ctxMenu.nodeId);
    if (node.length) cy.animate({ zoom: { level: 2.0, position: node.position() }, duration: 400 });
    setCtxMenu(null);
  };

  const handleCtxCopyAddress = () => {
    if (ctxMenu) navigator.clipboard.writeText(ctxMenu.nodeId);
    setCtxMenu(null);
  };

  const handleCtxHideNode = () => {
    if (!ctxMenu) return;
    const nodeId = ctxMenu.nodeId;
    const newNodes = new Set([...hiddenNodes, nodeId]);
    if (selectedNode?.id === nodeId) setSelectedNode(null);
    setCtxMenu(null);
    if (onViewStateChange) onViewStateChange(hiddenEdges, newNodes);
  };

  const handleRestoreAll = () => {
    if (onViewStateChange) onViewStateChange(new Set(), new Set());
  };

  const handleRestoreNodes = () => {
    if (onViewStateChange) onViewStateChange(hiddenEdges, new Set());
  };

  const handleRestoreEdges = () => {
    if (onViewStateChange) onViewStateChange(new Set(), hiddenNodes);
  };

  // ── Edge context menu handlers ────────────────────────────────────
  const handleCtxRemoveEdge = () => {
    if (!ctxMenu) return;
    const edgeId = ctxMenu.edgeId;
    const newEdges = new Set([...hiddenEdges, edgeId]);
    if (selectedEdge?.id === edgeId) setSelectedEdge(null);
    setCtxMenu(null);
    if (onViewStateChange) onViewStateChange(newEdges, hiddenNodes);
  };

  const handleCtxCopyTxHash = () => {
    if (ctxMenu?.edgeTxHash) navigator.clipboard.writeText(ctxMenu.edgeTxHash);
    setCtxMenu(null);
  };

  // ── Add-to-registry handlers ──────────────────────────────────────
  const handleCtxAddToRegistry = () => {
    if (!ctxMenu) return;
    const rawNode    = data?.nodes?.find(n => (n.id || n.address) === ctxMenu.nodeId);
    const blockchain = rawNode?.blockchain || 'tron';
    setRegForm({ address_type: 'contract', name: '', symbol: '', notes: '' });
    setRegModal({ address: ctxMenu.nodeId, blockchain });
    setRegError(null); setRegSuccess(null);
    setCtxMenu(null);
  };

  const handleRegSave = async () => {
    if (!regForm.name.trim()) { setRegError('Name is required'); return; }
    try {
      setRegSaving(true); setRegError(null);
      await knownAddressService.create({
        address:      regModal.address,
        blockchain:   regModal.blockchain,
        address_type: regForm.address_type,
        name:         regForm.name,
        symbol:       regForm.symbol,
        notes:        regForm.notes,
      });
      setRegSuccess('Added to registry!');
      const addrs = data.nodes.map(n => n.id || n.address).filter(Boolean);
      const res   = await knownAddressService.bulkLookup(addrs);
      setKnownMap(res.data || {});
      setTimeout(() => { setRegModal(null); setRegSuccess(null); }, 1200);
    } catch (e) {
      setRegError(e.response?.data?.detail || 'Save failed');
    } finally {
      setRegSaving(false);
    }
  };

  // ── Render helpers ───────────────────────────────────────────────
  const knownCount = Object.keys(knownMap).length;
  const pruneNodeCount = hiddenNodes.size;
  const pruneEdgeCount = hiddenEdges.size;

  return (
    <div className="graph-wrapper">

      {/* Toolbar */}
      <div className="graph-toolbar">
        {/* Search bar — find a node address or TX hash, select + centre + flash it */}
        <div className="graph-search-bar">
          <input
            type="text"
            className="graph-search-input"
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') handleSearch(); if (e.key === 'Escape') handleSearchClear(); }}
            placeholder="🔎 Search address or TX hash…"
          />
          <button className="graph-btn" onClick={handleSearch} disabled={!searchQuery.trim()}>Search</button>
          {searchQuery && (
            <button className="graph-btn" onClick={handleSearchClear} title="Clear search">✕</button>
          )}
        </div>

        <div className="graph-toolbar-sep" />

        {/* TX direction filter badge — visible when a filter is active, clearable from the canvas */}
        {txFilter !== 'all' && (
          <div className="graph-txfilter-badge">
            {txFilter === 'incoming' ? '↓ Incoming' : '↑ Outgoing'}
            <button
              className="graph-txfilter-clear"
              onClick={() => onTxFilterChange?.('all')}
              title="Clear direction filter"
            >✕</button>
          </div>
        )}

        {txFilter !== 'all' && <div className="graph-toolbar-sep" />}

        {/* Dust filter — hides low-value transactions often used to obfuscate a trace */}
        <div className="graph-dust-filter" title="Dim transactions below this value — scammers/mixers often spray low-value 'dust' transfers to obscure a trace. 0 = show everything.">
          <span className="graph-dust-label">🌫️ Dust &lt;</span>
          <input
            type="number"
            className="graph-dust-input"
            min="0"
            step="any"
            value={dustThreshold}
            onChange={e => setDustThreshold(Math.max(0, parseFloat(e.target.value) || 0))}
            title="Dim transactions with value below this number (0 = show all)"
          />
          {dustThreshold > 0 && (
            <button className="graph-btn graph-btn-date-clear" onClick={() => setDustThreshold(0)} title="Clear dust filter — show all transactions">✕</button>
          )}
        </div>

        <div className="graph-toolbar-sep" />

        {/* Timeframe filter — date range picker */}
        <div className="graph-date-filter">
          <span className="graph-date-label">📅</span>
          <input
            type="date"
            className="graph-date-input"
            value={dateFrom}
            onChange={e => onDateFromChange?.(e.target.value)}
            title="Filter transactions from this date"
          />
          <span className="graph-date-sep">→</span>
          <input
            type="date"
            className="graph-date-input"
            value={dateTo}
            onChange={e => onDateToChange?.(e.target.value)}
            title="Filter transactions to this date"
          />
          {(dateFrom || dateTo) && (
            <button
              className="graph-btn graph-btn-date-clear"
              onClick={() => { onDateFromChange?.(''); onDateToChange?.(''); }}
              title="Clear date filter"
            >✕</button>
          )}
        </div>

        <div className="graph-toolbar-sep" />

        {/* Layout mode switcher */}
        <div className="graph-layout-switcher">
          {LAYOUT_MODES.map(m => (
            <button
              key={m.key}
              className={`graph-layout-btn${layoutMode === m.key ? ' active' : ''}`}
              onClick={() => handleLayoutMode(m.key)}
              title={m.title}
            >
              {m.label}
            </button>
          ))}
        </div>

        <div className="graph-toolbar-sep" />

        <button className="graph-btn" onClick={handleFit}>🎯 Fit</button>
        <button className="graph-btn" onClick={handleReLayout}>⟳ Re-layout</button>
        {startAddress && (
          <button className="graph-btn highlight" onClick={handleFocusStart}>★ Focus Start</button>
        )}
        <button className="graph-btn" onClick={handleExport}>📥 Export PNG</button>
        <button className="graph-btn graph-btn-svg" onClick={handleExportSvg}>📐 Export SVG</button>
        {investigationId && (
          <button
            className="graph-btn graph-btn-datasheet"
            onClick={() => window.open(`/investigation/${investigationId}/datasheet`, '_blank')}
            title="Open visible nodes/transactions as a colour-coded spreadsheet in a new window, with Excel export"
          >
            📊 Data Sheet
          </button>
        )}
        {investigationId && (hiddenEdges.size > 0 || hiddenNodes.size > 0) && (
          <button
            className="graph-btn graph-btn-focused"
            onClick={() => window.open(`/investigation/${investigationId}/focused`, '_blank')}
            title="Open the pruned graph in a clean focused window"
          >
            🔍 Focused View
          </button>
        )}
        <button
          className="graph-btn graph-btn-prune-selected"
          onClick={handleHideSelected}
          disabled={!onViewStateChange || (selectedSummary.nodes === 0 && selectedSummary.edges === 0)}
          title={onViewStateChange
            ? 'Hide all highlighted nodes / transactions (Ctrl+click to multi-select)'
            : 'This view does not support hiding (missing onViewStateChange)'}
        >
          🙈 Hide highlighted
          {(selectedSummary.nodes > 0 || selectedSummary.edges > 0) ? ` (${selectedSummary.nodes}N / ${selectedSummary.edges}TX)` : ''}
        </button>
        {knownCount > 0 && (
          <span className="graph-known-badge">🗂️ {knownCount} known address{knownCount !== 1 ? 'es' : ''}</span>
        )}
        {pruneEdgeCount > 0 && (
          <button className="graph-btn graph-btn-restore" onClick={handleRestoreEdges}
            title="Restore all removed transactions">
            🔄 Restore {pruneEdgeCount} TX{pruneEdgeCount !== 1 ? 's' : ''}
          </button>
        )}
        {pruneNodeCount > 0 && (
          <button className="graph-btn graph-btn-restore" onClick={handleRestoreNodes}
            title="Restore all hidden addresses">
            👁 Restore {pruneNodeCount} address{pruneNodeCount !== 1 ? 'es' : ''}
          </button>
        )}
        {(pruneEdgeCount > 0 || pruneNodeCount > 0) && (
          <button className="graph-btn graph-btn-restore-all" onClick={handleRestoreAll}
            title="Restore everything">
            ♻ Restore All
          </button>
        )}
        {layoutRunning && <span className="graph-stabilizing">⚙ Laying out…</span>}
      </div>

      {searchError && (
        <div className="graph-search-error">⚠ {searchError}</div>
      )}

      {knownLookupFailed && (
        <div className="graph-search-error" title="The known-address registry lookup failed or timed out — the graph is showing without known-address colours/labels. Try Re-layout to retry.">
          ⚠ Known-address lookup failed or timed out — showing graph without registry labels.
        </div>
      )}

      {/* Cytoscape canvas — height controlled by Graph.css clamp() rule (fix #6) */}
      <CytoscapeComponent
        elements={elements}
        stylesheet={CY_STYLESHEET}
        cy={handleCyRef}
        className="graph-canvas"
        style={{ width: '100%' }}
        minZoom={0.1}
        maxZoom={5}
        wheelSensitivity={0.6}
      />

      {/* Tooltip — portaled to document.body, same reason as the context menus above: this is
          position:fixed but lives under .tab-content/.graph-container (backdrop-filter
          ancestors), so without the portal its "fixed" coords are relative to that blurred
          ancestor's box instead of the real viewport. */}
      {createPortal(
        <div ref={tooltipRef} className="cy-tooltip" style={{ display: 'none' }} />,
        document.body
      )}

      {/* Node and edge details are intentionally NOT rendered here as an in-canvas floating
          panel - they're shown exclusively in the parent's sidebar via onNodeSelect/onEdgeSelect
          above. This avoids needing to fight .graph-wrapper's overflow:hidden clipping. */}

      {/* Legend */}
      <div className="graph-legend">
        {startAddress && (
          <div className="legend-item">
            <div className="legend-dot legend-star" style={{ background: '#00d9ff', boxShadow: '0 0 6px #00d9ff' }} />
            <span>Start Address</span>
          </div>
        )}
        {monitoredAddresses.size > 0 && (
          <div className="legend-item">
            <div className="legend-dot" style={{ background: '#f97316', boxShadow: '0 0 5px #f97316' }} />
            <span>📡 Monitored</span>
          </div>
        )}
        {(layoutMode === 'tracer' || layoutMode === 'visualizer') && (
          <>
            <div className="legend-item">
              <div className="legend-dot" style={{ background: 'rgba(239,68,68,0.8)', borderRadius: 2 }} />
              <span>Outflow</span>
            </div>
            <div className="legend-item">
              <div className="legend-dot" style={{ background: 'rgba(74,222,128,0.8)', borderRadius: 2 }} />
              <span>Inflow</span>
            </div>
            <div className="legend-divider" />
          </>
        )}
        {Object.entries(KNOWN_COLORS).map(([type, color]) => {
          const hasAny = Object.values(knownMap).some(k => k.address_type === type);
          if (!hasAny) return null;
          return (
            <div key={type} className="legend-item">
              <div className="legend-dot legend-diamond" style={{ background: color }} />
              <span>{KNOWN_ICONS[type]} {type.charAt(0).toUpperCase() + type.slice(1)}</span>
            </div>
          );
        })}
        <div className="legend-divider" />
        <div className="legend-item"><div className="legend-dot" style={{ background: '#1e8c45' }} /><span>Low Risk</span></div>
        <div className="legend-item"><div className="legend-dot" style={{ background: '#e8c200' }} /><span>Medium</span></div>
        <div className="legend-item"><div className="legend-dot" style={{ background: '#ff8c00' }} /><span>High</span></div>
        <div className="legend-item"><div className="legend-dot" style={{ background: '#ff3b3b' }} /><span>Critical</span></div>
      </div>

      {/* Right-click context menu — NODE. Rendered via portal directly into document.body:
          position:fixed here is computed relative to the nearest ancestor with a
          backdrop-filter/transform/filter (an ancestor block becomes the containing
          block for fixed descendants in that case) — .tab-content and .graph-container
          both have backdrop-filter:blur(...), which was silently shifting this menu's
          fixed top/left away from real viewport coordinates and made it appear too far
          right/clipped. Portaling to body removes those filtered ancestors from the
          containment chain entirely, so position:fixed (and the window.innerWidth/
          innerHeight clamp math in the effect above) is relative to the real viewport again. */}
      {ctxMenu && ctxMenu.type === 'node' && createPortal(
        <div ref={ctxMenuRef} className="graph-ctx-menu" style={{ top: ctxMenu.y, left: ctxMenu.x }}
          onMouseLeave={() => setCtxMenu(null)}>
          <div className="ctx-addr">{ctxMenu.nodeId.substring(0, 16)}…</div>
          <button className="ctx-item" onClick={handleCtxFocus}>🔍 Focus node</button>
          <button className="ctx-item" onClick={handleCtxCopyAddress}>📋 Copy address</button>
          {ctxMenu.nodeId !== startAddress && (
            <button className="ctx-item ctx-item-prune" onClick={handleCtxHideNode}
              title="Hide this address and all its transactions from the graph (persisted)">
              🙈 Hide address
            </button>
          )}
          {!knownMap[ctxMenu.nodeId] ? (
            <button className="ctx-item ctx-item-highlight" onClick={handleCtxAddToRegistry}>🗂️ Add to Known Registry</button>
          ) : (
            <div className="ctx-item ctx-item-known">✓ Already in registry</div>
          )}
        </div>,
        document.body
      )}

      {/* Right-click context menu — EDGE. Also portaled to document.body, same reason as above. */}
      {ctxMenu && ctxMenu.type === 'edge' && createPortal(
        <div ref={ctxMenuRef} className="graph-ctx-menu" style={{ top: ctxMenu.y, left: ctxMenu.x }}
          onMouseLeave={() => setCtxMenu(null)}>
          <div className="ctx-addr" style={{ color: '#aac8ff' }}>
            ⇒ TX: {ctxMenu.edgeTxHash ? ctxMenu.edgeTxHash.substring(0, 14) + '…' : ctxMenu.edgeId.substring(0, 14) + '…'}
          </div>
          <div className="ctx-edge-detail">
            {formatValue(ctxMenu.edgeValue)} {ctxMenu.edgeToken}
          </div>
          <button className="ctx-item" onClick={handleCtxCopyTxHash}>📋 Copy TX hash</button>
          <button className="ctx-item ctx-item-prune" onClick={handleCtxRemoveEdge}
            title="Remove this transaction from the investigation graph permanently">
            🗑 Remove transaction
          </button>
        </div>,
        document.body
      )}

      {/* Add to registry modal */}
      {regModal && (
        <div className="graph-modal-overlay" onClick={() => setRegModal(null)}>
          <div className="graph-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>🗂️ Add to Known Registry</h3>
              <button className="modal-close" onClick={() => setRegModal(null)}>✕</button>
            </div>
            <code className="modal-addr">{regModal.address}</code>

            {regSuccess ? (
              <div className="modal-success">✓ {regSuccess}</div>
            ) : (
              <>
                <div className="modal-field">
                  <label>Blockchain</label>
                  <select value={regModal.blockchain}
                    onChange={e => setRegModal(m => ({ ...m, blockchain: e.target.value }))}
                    className="modal-select">
                    {BLOCKCHAINS.map(b => <option key={b} value={b}>{b}</option>)}
                  </select>
                </div>
                <div className="modal-field">
                  <label>Type *</label>
                  <select value={regForm.address_type}
                    onChange={e => setRegForm(f => ({ ...f, address_type: e.target.value }))}
                    className="modal-select"
                    style={{ color: KNOWN_COLORS[regForm.address_type] }}>
                    {ADDRESS_TYPES.map(t => (
                      <option key={t} value={t} style={{ color: KNOWN_COLORS[t] }}>{KNOWN_ICONS[t]} {t}</option>
                    ))}
                  </select>
                </div>
                <div className="modal-field">
                  <label>Name *</label>
                  <input className="modal-input" value={regForm.name}
                    onChange={e => setRegForm(f => ({ ...f, name: e.target.value }))}
                    placeholder="e.g. Tether USD, Binance Wallet" />
                </div>
                <div className="modal-field">
                  <label>Symbol</label>
                  <input className="modal-input" value={regForm.symbol}
                    onChange={e => setRegForm(f => ({ ...f, symbol: e.target.value }))}
                    placeholder="e.g. USDT" />
                </div>
                <div className="modal-field">
                  <label>Notes</label>
                  <input className="modal-input" value={regForm.notes}
                    onChange={e => setRegForm(f => ({ ...f, notes: e.target.value }))}
                    placeholder="Optional notes" />
                </div>
                {regError && <div className="modal-error">⚠ {regError}</div>}
                <div className="modal-actions">
                  <button className="modal-btn-cancel" onClick={() => setRegModal(null)}>Cancel</button>
                  <button className="modal-btn-save" onClick={handleRegSave} disabled={regSaving}>
                    {regSaving ? '⏳ Saving…' : '✓ Add to Registry'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
